import React, {createContext, ReactNode, useContext, useState} from 'react';
import ErrorAlert from "./ErrorAlert";
import ConfirmAlert from "../alert/ConfirmAlert";

// AlertContext에서 제공할 메서드들의 타입 정의
interface AlertContextType {
    showAlert: (message: string, type?: 'error' | 'info') => void;  // 알림 표시 함수
    hideAlert: () => void;  // 알림 숨김 함수
    showConfirm: (message: string, onConfirm: () => void, onCancel?: () => void) => void;  // 확인 대화상자 표시 함수
    hideConfirm: () => void;  // 확인 대화상자 숨김 함수
}

// Alert Context 생성
const AlertContext = createContext<AlertContextType | null>(null);

// 전역에서 알림을 표시하기 위한 함수 참조 변수들
// Provider 외부에서도 알림을 표시할 수 있도록 함
let showAlertFn: ((message: string, type?: 'error' | 'info') => void) | null = null;
let showConfirmFn: ((message: string, onConfirm: () => void, onCancel?: () => void) => void) | null = null;

// 알림 상태 인터페이스
interface AlertState {
    message: string;      // 알림 메시지
    type: 'error' | 'info';  // 알림 타입
    isVisible: boolean;   // 알림 표시 여부
}

// 확인 대화상자 상태 인터페이스
interface ConfirmState {
    message: string;      // 확인 메시지
    isVisible: boolean;   // 대화상자 표시 여부
    onConfirm: () => void;  // 확인 시 실행할 콜백
    onCancel?: () => void;  // 취소 시 실행할 콜백 (선택적)
}

// Alert Provider 컴포넌트
export const AlertProvider = ({children}: { children: ReactNode }) => {
    // 알림 상태 관리
    const [alert, setAlert] = useState<AlertState>({
        message: '',
        type: 'error',
        isVisible: false,
    });

    // 확인 대화상자 상태 관리
    const [confirm, setConfirm] = useState<ConfirmState>({
        message: '',
        isVisible: false,
        onConfirm: () => {
        },
    });

    // 알림 표시 함수
    const showAlert = (message: string, type: 'error' | 'info' = 'error') => {
        setAlert({message, type, isVisible: true});
    };

    // 알림 숨김 함수
    const hideAlert = () => {
        setAlert(prev => ({...prev, isVisible: false}));
    };

    // 확인 대화상자 표시 함수
    const showConfirm = (message: string, onConfirm: () => void, onCancel?: () => void) => {
        setConfirm({
            message,
            isVisible: true,
            onConfirm,
            onCancel
        });
    };

    // 확인 대화상자 숨김 함수
    const hideConfirm = () => {
        setConfirm(prev => ({...prev, isVisible: false}));
    };

    // 확인 버튼 클릭 처리 함수
    const handleConfirm = () => {
        confirm.onConfirm();  // 확인 콜백 실행
        hideConfirm();       // 대화상자 숨김
    };

    // 취소 버튼 클릭 처리 함수
    const handleCancel = () => {
        if (confirm.onCancel) {
            confirm.onCancel();  // 취소 콜백이 있는 경우 실행
        }
        hideConfirm();         // 대화상자 숨김
    };

    // 전역 함수 참조 설정
    showAlertFn = showAlert;
    showConfirmFn = showConfirm;

    return (
        <AlertContext.Provider value={{showAlert, hideAlert, showConfirm, hideConfirm}}>
            {children}
            {/* 알림 컴포넌트 조건부 렌더링 */}
            {alert.isVisible && (
                <ErrorAlert
                    message={alert.message}
                    type={alert.type}
                    onClose={hideAlert}
                />
            )}
            {/* 확인 대화상자 컴포넌트 조건부 렌더링 */}
            {confirm.isVisible && (
                <ConfirmAlert
                    message={confirm.message}
                    onConfirm={handleConfirm}
                    onCancel={handleCancel}
                />
            )}
        </AlertContext.Provider>
    );
};

// 전역에서 사용할 수 있는 알림 표시 함수
export const globalShowAlert = (message: string, type: 'error' | 'info' = 'error') => {
    if (showAlertFn) {
        showAlertFn(message, type);
    }
};

// 전역에서 사용할 수 있는 확인 대화상자 표시 함수
export const globalShowConfirm = (message: string, onConfirm: () => void, onCancel?: () => void) => {
    if (showConfirmFn) {
        showConfirmFn(message, onConfirm, onCancel);
    }
};

// Alert Context를 사용하기 위한 커스텀 훅
export const useAlert = () => {
    const context = useContext(AlertContext);
    if (!context) {
        throw new Error('useAlert must be used within an AlertProvider');
    }
    return context;
};